import { DOCUMENT_CONSTANTS } from './constants/MilestoneConstants';

function getDocumentLimitError(maxDocuments, newFileCount, availableSlots) {
    const base = `Only a maximum of ${maxDocuments} documents can be uploaded.`;
    if (availableSlots <= 0) {
        return base;
    }
    const slotLabel = availableSlots === 1 ? 'slot' : 'slots';
    const verb = availableSlots === 1 ? 'is' : 'are';
    return `${base} You are trying to upload ${newFileCount} files but only ${availableSlots} ${slotLabel} ${verb} available.`;
}

/**
 * Validates file type for deal financing
 * @param {File} file - File to validate
 * @returns {Object} - Validation result with error message
 */
export const validateFileType = (file) => {
    const fileName = file.name.toLowerCase();
    const isValid = DOCUMENT_CONSTANTS.ALLOWED_FILE_TYPES.some(type => 
        fileName.endsWith(type.toLowerCase())
    );
    
    if (!isValid) {
        return {
            isValid: false,
            error: 'Only allowed file formats are PDF, JPEG, PNG'
        };
    }
    
    return { isValid: true };
};

/**
 * Validates document upload limits and duplicates
 * @param {Array} newFiles - Array of new files to be uploaded
 * @param {Array} existingFiles - Array of already selected files
 * @param {number} maxDocuments - Maximum number of documents allowed (default: 3)
 * @returns {Object} - Validation result with errors array
 */
export const validateDocumentUpload = (newFiles, existingFiles = [], maxDocuments = DOCUMENT_CONSTANTS.MAX_DOCUMENTS_PER_PAYMENT) => {
    const errors = [];
    const currentFileCount = existingFiles.length;
    const totalFileCount = currentFileCount + newFiles.length;

    // Check document limit
    if (totalFileCount > maxDocuments) {
        const availableSlots = maxDocuments - currentFileCount;
        errors.push({
            name: 'limit_error',
            error: getDocumentLimitError(maxDocuments, newFiles.length, availableSlots)
        });
        
        return { isValid: false, errors };
    }

    // Check for duplicate files
    const existingFileNames = existingFiles.map(file => file.name.toLowerCase());
    const duplicateFiles = newFiles.filter(file => existingFileNames.includes(file.name.toLowerCase()));

    if (duplicateFiles.length > 0) {
        const duplicateNames = duplicateFiles.map(f => f.name).join(', ');
        errors.push({
            name: 'duplicate_error',
            error: `Duplicate files are not allowed. Found: ${duplicateNames}`
        });
        
        return { isValid: false, errors };
    }

    // Check file size
    const oversizedFiles = newFiles.filter(file => file.size > DOCUMENT_CONSTANTS.MAX_FILE_SIZE_BYTES);
    if (oversizedFiles.length > 0) {
        oversizedFiles.forEach(file => {
            errors.push({
                name: file.name,
                error: `File "${file.name}" exceeds ${DOCUMENT_CONSTANTS.MAX_FILE_SIZE_MB} MB.`
            });
        });
    }

    return { 
        isValid: errors.length === 0, 
        errors,
        validFiles: newFiles.filter(file => file.size <= DOCUMENT_CONSTANTS.MAX_FILE_SIZE_BYTES)
    };
};

/**
 * Validates if upload is disabled based on current file count
 * @param {Array} selectedFiles - Array of currently selected files
 * @param {number} maxDocuments - Maximum number of documents allowed (default: 3)
 * @returns {boolean} - True if upload should be disabled
 */
export const isUploadDisabled = (selectedFiles = [], maxDocuments = DOCUMENT_CONSTANTS.MAX_DOCUMENTS_PER_PAYMENT) => {
    return selectedFiles.length >= maxDocuments;
};

/**
 * Gets the appropriate error message for upload state
 * @param {Array} selectedFiles - Array of currently selected files
 * @param {number} maxDocuments - Maximum number of documents allowed (default: 3)
 * @returns {string} - Error message or empty string
 */
export const getUploadErrorMessage = (selectedFiles = [], maxDocuments = DOCUMENT_CONSTANTS.MAX_DOCUMENTS_PER_PAYMENT) => {
    if (selectedFiles.length >= maxDocuments) {
        return `Maximum ${maxDocuments} files uploaded`;
    }
    return '';
};

/**
 * Formats file size for display
 * @param {number} bytes - File size in bytes
 * @returns {string} - Formatted file size string
 */
export const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

/**
 * Validates file type
 * @param {File} file - File to validate
 * @returns {boolean} - True if file type is allowed
 */
export const isValidFileType = (file) => {
    const fileName = file.name.toLowerCase();
    return DOCUMENT_CONSTANTS.ALLOWED_FILE_TYPES.some(type => 
        fileName.endsWith(type.toLowerCase())
    );
};

/**
 * Gets validation error summary message
 * @param {Array} errors - Array of validation errors
 * @returns {string} - Summary error message
 */
export const getErrorSummaryMessage = (errors) => {
    if (!errors || errors.length === 0) return '';
    
    const errorFiles = errors.filter(error => error.error);
    const duplicateErrors = errorFiles.filter(error => error.name === 'duplicate_error');
    const limitErrors = errorFiles.filter(error => error.name === 'limit_error');
    
    if (duplicateErrors.length > 0) {
        return 'Duplicate files are not allowed';
    } else if (limitErrors.length > 0) {
        return limitErrors[0].error;
    } else {
        return errorFiles.length === 1 
            ? '1 file could not be uploaded' 
            : `${errorFiles.length} files could not be uploaded`;
    }
};
